import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ActionCardComponent } from '@shared/components/action-card/action-card.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';

import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { TipoAmbienteEnum } from '@shared/enums/dfe/tipo-ambiente.enum';
import { StatusServicoSefazEnum } from '@shared/enums/dfe/status-servico-sefaz.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { ConfiguracaoDFeGeralService } from '@shared/services/apis/dfe/configuracao-dfe-geral.service';
import { ConfiguracaoDFeGeralService as OrquestradorConfiguracaoDFeGeralService } from '@shared/services/apis/orquestrador-dfe/configuracao-dfe-geral.service';
import { ConfiguracaoDFeGeralStruct } from '@shared/services/structs/dfe/configuracao-dfe-geral.struct';
import { ConfiguracaoDFeGeralRequest } from '@shared/services/requests/dfe/configuracao-dfe-geral.request';
import { SimularConfiguracaoDFeGeralResponse } from '@shared/services/responses/dfe/simular-configuracao-dfe-geral.response';
import { TestarAmbienteConfiguracaoDFeGeralResponse } from '@shared/services/responses/orquestrador-dfe/testar-ambiente-configuracao-dfe-geral.response';

/** Card de seleção de ambiente (dado local de tela). */
type OpcaoAmbiente = {
  idTipoAmbiente: TipoAmbienteEnum;
  titulo: string;
  consequencia: string;
  icone: string;
};

/** Resultado do "testar ambiente" já traduzido para o push padrão. */
type ResultadoTesteAmbiente = {
  tipo: AlertType;
  titulo: string;
  mensagem: string;
};

@Component({
  selector: 'app-config-geral-notas-fiscais',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    HeaderComponent,
    ActionCardComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
  ],
  providers: [DatePipe],
  templateUrl: './config-geral-notas-fiscais.component.html',
  styleUrls: ['./config-geral-notas-fiscais.component.css'],
})
export class ConfigGeralNotasFiscaisComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;

  public podeVisualizar = false;
  public podeAlterar = false;

  public isLoading = false;
  public isSaving = false;
  public isTestando = false;
  public configCarregada = false;

  private configuracao: ConfiguracaoDFeGeralStruct | null = null;
  public idTipoAmbienteSalvo: TipoAmbienteEnum | null = null;
  public idTipoAmbienteSelecionado: TipoAmbienteEnum | null = null;

  /** Modelos liberados para os emissores da empresa (a tela edita uma cópia do salvo). */
  public emiteNFeSalvo = false;
  public emiteNFeSelecionado = false;
  public emiteNFCeSalvo = false;
  public emiteNFCeSelecionado = false;

  public readonly opcoesAmbiente: OpcaoAmbiente[] = [
    {
      idTipoAmbiente: TipoAmbienteEnum.Homologacao,
      titulo: 'Homologação',
      consequencia: 'As notas emitidas não têm validade jurídica. Use para testar o fluxo antes de operar.',
      icone: 'science',
    },
    {
      idTipoAmbiente: TipoAmbienteEnum.Producao,
      titulo: 'Produção',
      consequencia: 'As notas emitidas têm validade jurídica e geram obrigação fiscal.',
      icone: 'verified',
    },
  ];

  public readonly tooltipVoltar = 'Voltar para Configurações de Notas Fiscais';

  constructor(
    private configuracaoDFeGeralService: ConfiguracaoDFeGeralService,
    private orquestradorConfiguracaoDFeGeralService: OrquestradorConfiguracaoDFeGeralService,
    private authService: AuthService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private router: Router
  ) {}

  ngOnInit(): void {
    const permissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];
    this.podeVisualizar = permissoes.includes(PermissaoEnum.Configuracoes_NotasFiscais_Visualizar);
    this.podeAlterar = permissoes.includes(PermissaoEnum.Configuracoes_NotasFiscais_Alterar);
    if (!this.podeVisualizar) return;
    this.carregar();
  }

  public carregar(): void {
    this.isLoading = true;
    this.configCarregada = false;
    this.configuracaoDFeGeralService.get().subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError || !response.configuracaoDFeGeral) {
          this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar o ambiente da nota fiscal.', AlertType.error);
          return;
        }
        this.configCarregada = true;
        this.configuracao = response.configuracaoDFeGeral;
        this.idTipoAmbienteSalvo = response.configuracaoDFeGeral.idTipoAmbiente;
        this.idTipoAmbienteSelecionado = response.configuracaoDFeGeral.idTipoAmbiente;
        this.aplicarModelosSalvos(response.configuracaoDFeGeral);
      },
      error: error => {
        this.isLoading = false;
        this.alertService.show('Erro', error?.message || 'Falha na comunicação com o servidor.', AlertType.error);
      },
    });
  }

  public get houveAlteracao(): boolean {
    return this.houveAlteracaoAmbiente || this.houveAlteracaoModelos;
  }

  public get houveAlteracaoAmbiente(): boolean {
    return this.idTipoAmbienteSelecionado !== this.idTipoAmbienteSalvo;
  }

  public get houveAlteracaoModelos(): boolean {
    return this.emiteNFeSelecionado !== this.emiteNFeSalvo || this.emiteNFCeSelecionado !== this.emiteNFCeSalvo;
  }

  private aplicarModelosSalvos(configuracao: ConfiguracaoDFeGeralStruct): void {
    this.emiteNFeSalvo = configuracao.emiteNFe;
    this.emiteNFeSelecionado = configuracao.emiteNFe;
    this.emiteNFCeSalvo = configuracao.emiteNFCe;
    this.emiteNFCeSelecionado = configuracao.emiteNFCe;
  }

  public get bloquearSelecao(): boolean {
    return !this.podeAlterar || !this.configCarregada || this.isSaving;
  }

  public selecionar(idTipoAmbiente: TipoAmbienteEnum): void {
    if (this.bloquearSelecao || idTipoAmbiente === this.idTipoAmbienteSelecionado) return;
    this.idTipoAmbienteSelecionado = idTipoAmbiente;
  }

  public testarAmbiente(): void {
    if (this.isTestando || this.idTipoAmbienteSelecionado == null) return;
    const idTipoAmbienteTestado = this.idTipoAmbienteSelecionado;
    this.isTestando = true;
    this.orquestradorConfiguracaoDFeGeralService.testarAmbiente({ idTipoAmbiente: idTipoAmbienteTestado }).subscribe({
      next: response => {
        this.isTestando = false;
        // Descarta o retorno se o usuário trocou de ambiente durante a consulta.
        if (idTipoAmbienteTestado !== this.idTipoAmbienteSelecionado) return;
        const resultado = this.traduzirResultadoTeste(response, idTipoAmbienteTestado);
        this.alertService.show(resultado.titulo, resultado.mensagem, resultado.tipo);
      },
      error: error => {
        this.isTestando = false;
        if (idTipoAmbienteTestado !== this.idTipoAmbienteSelecionado) return;
        this.alertService.show(
          'Não foi possível consultar a Sefaz',
          error?.message || 'Falha na comunicação com o servidor.',
          AlertType.error
        );
      },
    });
  }

  private traduzirResultadoTeste(
    response: TestarAmbienteConfiguracaoDFeGeralResponse,
    idTipoAmbiente: TipoAmbienteEnum
  ): ResultadoTesteAmbiente {
    const ambiente = this.nomeAmbiente(idTipoAmbiente);
    const retornoSefaz = response.codigoRetornoSefaz != null
      ? `Retorno da Sefaz: ${response.codigoRetornoSefaz} - ${response.mensagemRetornoSefaz ?? ''}`.trim()
      : response.mensagemRetornoSefaz ?? '';

    // O certificado vem antes do erro genérico: sem certificado válido a API também devolve isError.
    if (!response.possuiCertificado) {
      const vencimento = response.dataVencimentoCertificado
        ? ` O certificado venceu em ${this.datePipe.transform(response.dataVencimentoCertificado, 'dd/MM/yyyy')}.`
        : '';
      return {
        tipo: AlertType.error,
        titulo: 'Certificado digital recusado',
        mensagem: (response.errorDescription || response.mensagemRetornoSefaz || 'A empresa não possui certificado digital válido para consultar a Sefaz.') + vencimento,
      };
    }

    if (response.isError) {
      return {
        tipo: AlertType.error,
        titulo: `Não foi possível consultar a Sefaz em ${ambiente}`,
        mensagem: response.errorDescription || retornoSefaz,
      };
    }

    switch (response.statusServicoSefaz) {
      case StatusServicoSefazEnum.EmOperacao:
        return { tipo: AlertType.success, titulo: `Serviço em operação em ${ambiente}`, mensagem: retornoSefaz };
      case StatusServicoSefazEnum.ParalisadoTemporariamente:
        return { tipo: AlertType.warning, titulo: `Serviço paralisado temporariamente em ${ambiente}`, mensagem: retornoSefaz };
      case StatusServicoSefazEnum.ParalisadoSemPrevisao:
        return { tipo: AlertType.warning, titulo: `Serviço paralisado sem previsão de retorno em ${ambiente}`, mensagem: retornoSefaz };
      default:
        return { tipo: AlertType.warning, titulo: `Serviço indisponível em ${ambiente}`, mensagem: retornoSefaz };
    }
  }

  public salvar(): void {
    if (!this.podeAlterar || !this.houveAlteracao || this.isSaving || !this.configuracao || this.idTipoAmbienteSelecionado == null) return;
    const idTipoAmbiente = this.idTipoAmbienteSelecionado;
    this.isSaving = true;
    // Só os modelos mudaram: não há emissor que acompanhe, então não há simulação nem confirmação.
    if (!this.houveAlteracaoAmbiente) {
      this.gravar(idTipoAmbiente);
      return;
    }
    this.configuracaoDFeGeralService.simular({ idTipoAmbiente }).subscribe({
      next: simulacao => {
        if (simulacao.isError) {
          this.isSaving = false;
          this.alertService.show('Aviso', simulacao.errorDescription, AlertType.warning);
          return;
        }
        if (simulacao.conflitos?.length) {
          this.isSaving = false;
          this.abrirConflitos(simulacao, idTipoAmbiente);
          return;
        }
        this.confirmarSalvamento(simulacao, idTipoAmbiente);
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error?.message || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  private abrirConflitos(simulacao: SimularConfiguracaoDFeGeralResponse, idTipoAmbiente: TipoAmbienteEnum): void {
    const linhas = simulacao.conflitos
      .map(c => `&bull; <strong>${this.escaparHtml(c.nomeEmissorAutomatico)}</strong> e <strong>${this.escaparHtml(c.nomeEmissorFixo)}</strong>`
        + ` usariam a série ${c.serie} da ${this.escaparHtml(c.modelo)}`)
      .join('<br>');
    this.dialog.open(ConfirmModalComponent, {
      maxWidth: '96vw',
      data: {
        title: 'Não é possível trocar o ambiente',
        innerHtml: `Em ${this.nomeAmbiente(idTipoAmbiente)}, os emissores abaixo ficariam com a mesma série e o mesmo modelo.`
          + ` Altere a série de um deles no cadastro de emissores antes de trocar o ambiente.<br><br>${linhas}`,
        buttonClose: 'Fechar',
      },
    });
  }

  private confirmarSalvamento(simulacao: SimularConfiguracaoDFeGeralResponse, idTipoAmbiente: TipoAmbienteEnum): void {
    const quantidade = simulacao.quantidadeEmissoresAutomaticos ?? 0;
    let html = quantidade === 0
      ? 'Nenhum emissor está configurado como Automático. Portanto, nenhum emissor mudará de ambiente.'
      : quantidade === 1
        ? 'O emissor em modo Automático acompanhará esta configuração. A numeração dele continuará a partir do último número emitido.'
        : 'Os emissores em modo Automático acompanharão esta configuração. As numerações continuarão a partir dos últimos números emitidos.';

    const nomesNFCe = simulacao.emissoresComNFCeAtiva ?? [];
    if (nomesNFCe.length) {
      html += '<br><br><strong>Com NFC-e ativa</strong> (a NFC-e também muda de ambiente): '
        + nomesNFCe.map(nome => `<strong>${this.escaparHtml(nome)}</strong>`).join(', ') + '.';
    }

    const avisoProducao = idTipoAmbiente === TipoAmbienteEnum.Producao
      ? 'Em produção, as notas emitidas passam a ter validade jurídica e geram obrigação fiscal.'
      : '';

    this.dialog.open(ConfirmModalComponent, {
      maxWidth: '96vw',
      data: {
        title: 'Confirma a alteração do ambiente?',
        icon: 'dns',
        innerHtml: html,
        warningMessage: avisoProducao,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    }).afterClosed().subscribe(result => {
      if (!result?.confirm) {
        this.isSaving = false;
        return;
      }
      this.gravar(idTipoAmbiente);
    });
  }

  private gravar(idTipoAmbiente: TipoAmbienteEnum): void {
    if (!this.configuracao) return;
    const request: ConfiguracaoDFeGeralRequest = {
      idTipoAmbiente,
      emiteNFe: this.emiteNFeSelecionado,
      emiteNFCe: this.emiteNFCeSelecionado,
    };
    this.configuracaoDFeGeralService.atualizar(request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        const trocouAmbiente = idTipoAmbiente !== this.idTipoAmbienteSalvo;
        if (response.configuracaoDFeGeral) {
          this.configuracao = response.configuracaoDFeGeral;
          this.aplicarModelosSalvos(response.configuracaoDFeGeral);
        }
        this.idTipoAmbienteSalvo = idTipoAmbiente;
        this.configuracaoDFeGeralService.notificarAlteracao();
        this.alertService.show(
          'Sucesso',
          trocouAmbiente
            ? `Ambiente ${this.nomeAmbiente(idTipoAmbiente)} salvo. Ele vale para as próximas emissões.`
            : 'Configurações gerais salvas.',
          AlertType.success
        );
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error?.message || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public cancelar(): void {
    if (!this.houveAlteracao) return;
    this.idTipoAmbienteSelecionado = this.idTipoAmbienteSalvo;
    this.emiteNFeSelecionado = this.emiteNFeSalvo;
    this.emiteNFCeSelecionado = this.emiteNFCeSalvo;
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/notas-fiscais']);
  }

  public nomeAmbiente(idTipoAmbiente: TipoAmbienteEnum | null): string {
    return this.opcoesAmbiente.find(o => o.idTipoAmbiente === idTipoAmbiente)?.titulo ?? '';
  }

  private escaparHtml(valor: string): string {
    return (valor ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
